import { Alert, Box, Card, Heading, SimpleGrid, Skeleton, VStack } from '@chakra-ui/react';
import { BellAlertIcon } from '@heroicons/react/24/outline';
import { useFocusedBranchContext } from '@packages/core/branch/focused-branch-context';
import { useFocusedCompanyContext } from '@packages/core/company/focused-company-context';
import { formatCurrency } from '@packages/core/utils/currency-formatter';
import { useTranslate } from '@tolgee/react';
import { useEffect, useState } from 'react';
import { DateRange } from '../../common/reservation-analytic-service';
import SkeletonCard from '../../common/skeleton-card';
import MetricCard from '../../reservation/component/metric-card';
import useGetAppointmentOverviewMetric from '../logic/use-get-overview-appointment-report';

export default function AppointmentOverview(props: { dateRange: DateRange }) {
  const branch = useFocusedBranchContext();
  const company = useFocusedCompanyContext();

  const { t } = useTranslate();

  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: props.dateRange.startDate,
    endDate: props.dateRange.endDate,
  });

  useEffect(() => {
    setDateRange({
      startDate: props.dateRange.startDate,
      endDate: props.dateRange.endDate,
    });
  }, [props.dateRange]);

  const { analytics, isLoading, error } = useGetAppointmentOverviewMetric(branch.branch?.id ?? '', dateRange);

  if (isLoading) {
    return (
      <VStack gap={4} align="stretch">
        <Skeleton height="40px" />
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} gap={6}>
          {Array.from({ length: 4 }).map((_, index) => (
            <SkeletonCard key={index} />
          ))}
        </SimpleGrid>
      </VStack>
    );
  }

  if (error) {
    return (
      <Alert.Root status="error" borderRadius="md">
        <BellAlertIcon className="w-4 h-4" />
        <Box>
          <Alert.Title>Error loading analytics!</Alert.Title>
          <Alert.Description>{error.message}</Alert.Description>
        </Box>
      </Alert.Root>
    );
  }

  if (!analytics) {
    return (
      <Alert.Root status="info" borderRadius="md">
        <BellAlertIcon className="w-4 h-4" />
        <Alert.Title>No data available</Alert.Title>
      </Alert.Root>
    );
  }

  const { current, trends } = analytics;

  return (
    <Card.Root>
      <Card.Header>
        <Card.Title>
          <Heading fontWeight={'semibold'} fontSize="xl" color="gray.900">
            {t('appointment.report.overview', 'Overview')}
          </Heading>
        </Card.Title>
      </Card.Header>
      <Card.Body p={0}>
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} gap={4}>
          <MetricCard
            title={t('appointment.report.overview.metric.totalRevenue.title', 'Total Revenue')}
            value={formatCurrency(current.totalRevenue, company.settings.currency)}
            trend={trends.totalRevenue}
            subtitle={t('appointment.report.overview.metric.totalRevenue.subtitle', 'revenue')}
          />

          <MetricCard
            title={t(
              'appointment.report.overview.metric.averageRevenuePerAppointment.title',
              'Average Revenue per Appointment',
            )}
            value={formatCurrency(current.averageRevenuePerAppointment, company.settings.currency)}
            trend={trends.averageRevenuePerAppointment}
            subtitle={t(
              'appointment.report.overview.metric.averageRevenuePerAppointment.subtitle',
              'average revenue per appointment',
            )}
          />

          <MetricCard
            title={t(
              'appointment.report.overview.metric.totalConfirmedAppointments.title',
              'Total Confirmed Appointments',
            )}
            value={current.totalConfirmedAppointments}
            trend={trends.totalConfirmedAppointments}
            subtitle={t(
              'appointment.report.overview.metric.totalConfirmedAppointments.subtitle',
              'confirmed appointments',
            )}
          />

          <MetricCard
            title={t('appointment.report.overview.metric.bookingPerformanceRate.title', 'Booking Performance Rate')}
            value={`${current.bookingPerformanceRate.toString()}%`}
            trend={trends.bookingPerformanceRate}
            subtitle={t(
              'appointment.report.overview.metric.bookingPerformanceRate.subtitle',
              'booking performance rate',
            )}
          />
        </SimpleGrid>
      </Card.Body>
    </Card.Root>
  );
}
